import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link, Redirect, Route, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';

import {
  Box,
  Button,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Tabs
} from '@material-ui/core';
import { ArrowDropDown as ArrowDropDownIcon } from '@material-ui/icons';

import { ApiParams, FetchError } from '../../common/api/api';
import ConfirmDialog from '../../common/dialogs/ConfirmDialog';
import AppSnackbar, { AppSnackbarProps } from '../../common/AppSnackbar';
import HeaderBackButton, {
  StyledPageTemplate as StyledPageTemplateHeader
} from '../../common/HeaderBackButton';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import PageTemplateNav from '../../common/page-template/PageTemplateNav';
import SearchBox from '../../common/SearchBox';
import TaskStatusActions from '../tasks/TaskStatusActions';
import ProductBoard from './board/ProductBoard';
import ProductBoardSwimlane from './board/ProductBoardSwimlane';
import ProductBoardTrace from './board/ProductBoardTrace';
import ManageTemplateCard from './cards/ManageTemplateCard';
import ProductBoardFilter, {
  ProductFilterOptions
} from './components/ProductBoardFilter';
import ProductExportButton from './components/ProductExportButton';
import ProductHeaderTitle from './components/ProductHeaderTitle';
import ProductEntityCreateButton from './components/ProductEntityCreateButton';
import UserNeedsTable from './tables/UserNeedsTable';
import DesignInputsTable from './tables/DesignInputsTable';
import DesignOutputsTable from './tables/DesignOutputsTable';
import ValidationsTable from './tables/ValidationsTable';
import VerificationsTable from './tables/VerificationsTable';
import DesignSpecificationTable from './tables/DesignSpecificationTable';
import {
  useDeleteProduct,
  useFindProduct,
  useProductFilterQueryParams,
  useUpdateProduct
} from './Product.hooks';
import { BaseProductEntityTableProps, Product } from './Product.interface';
import { getProductEntityName } from './Product.config';
import ProductDetailPage from './ProductDetailPage';
import CartIconBadge from '../../common/cart/CartIconBadge';
import environment from '../../environments';
import HazardsTable from './tables/HazardsTable';
import ProductHazardBoardTrace from './board/ProductHazardBoardTrace';
import { useHazardFilterQueryParams } from '../hazards/Hazard.hooks';
import HazardBoardFilter, {
  HazardFilterOptions
} from '../hazards/components/HazardBoardFilter';
import ListMoreButtonMenu from '../../common/ListMoreButtonMenu';

export type PageType =
  | 'detail'
  | 'settings'
  | 'board'
  | 'swimlane'
  | 'trace'
  | 'hazardTrace'
  | 'userNeed'
  | 'designInput'
  | 'designOutput'
  | 'verification'
  | 'validation'
  | 'designSpec'
  | 'hazard';

const StyledPageTemplate = styled(StyledPageTemplateHeader as React.FC)`
  .page-template-dashboard.board-view {
    padding: 0;
  }
  .export-button-container {
    margin-left: 10px;
  }
`;

const ProductPage: React.FC = () => {
  const history = useHistory();
  const { view = '', productId: paramProductId = '' } = useParams<{
    view?: PageType;
    productId?: string;
  }>();
  const productId: number = parseInt(paramProductId) || 0;
  const fetchRef = useRef<Function | undefined>();
  const fetchTemplatesRef = useRef<Function | undefined>();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const productFilter = useProductFilterQueryParams();
  const hazardFilter = useHazardFilterQueryParams();
  const filter = view === 'hazardTrace' ? hazardFilter : productFilter;
  const views = [
    {
      name: 'Product Traceability',
      path: 'trace'
    },
    { name: 'Board View', path: 'board' },
    { name: 'List View', path: 'detail' },
    { name: 'Hazard Traceability', path: 'hazardTrace' },
    { name: 'Hazard List', path: 'hazard' }
  ];
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [product, setProduct] = useState<Product>();
  const [apiParams, setApiParams] = useState<ApiParams>({
    search,
    ...filter.getParams()
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: AppSnackbarProps['severity'];
    message?: string;
  }>({ open: false, severity: 'success' });
  const { product: foundProduct } = useFindProduct(productId, !!view);
  const { updatedProduct, updateError, updateProduct } =
    useUpdateProduct(productId);
  const {
    deletedProduct,
    error: deleteError,
    deleteProduct
  } = useDeleteProduct(productId);

  const tableProps: BaseProductEntityTableProps = {
    fetchRef,
    apiParams,
    productId: product?.id || 0
  };

  const isDetailPage: boolean = useMemo(() => {
    return [
      'detail',
      'settings',
      'userNeed',
      'designInput',
      'designOutput',
      'validation',
      'verification',
      'designSpec',
      'hazard'
    ].includes(view);
  }, [view]);

  const setError = (error: FetchError) => {
    setSnackbar({ open: true, severity: 'error', message: error.message });
  };

  const onUpdate = (product: Product) => {
    const label = product.deleted ? 'archived' : 'unarchived';
    setProduct(product);
    setSnackbar({
      open: true,
      severity: 'success',
      message: `Product ${label} successfully.`
    });
  };

  const getPageUrl = useCallback(
    (page: PageType) => {
      return `/portal/products/${productId}/${page}`;
    },
    [productId]
  );

  const gotoPage = (page: PageType) => history.push(getPageUrl(page));

  const onChange = (updatedProduct: Partial<Product>) => {
    product && setProduct({ ...product, ...updatedProduct });
  };

  useEffect(() => {
    if (foundProduct) {
      setProduct(foundProduct);
    }
  }, [foundProduct]);

  useEffect(() => {
    const params: ApiParams = { ...apiParams, search };
    !search && delete params.search;
    if (search || apiParams.search) {
      setApiParams(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    setApiParams({ search, ...filter.getParams() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.getParams]);

  useEffect(() => {
    updatedProduct && onUpdate(updatedProduct);
  }, [updatedProduct]);

  useEffect(() => {
    deletedProduct && onUpdate(deletedProduct);
  }, [deletedProduct]);

  useEffect(() => {
    updateError && setError(updateError);
  }, [updateError]);

  useEffect(() => {
    deleteError && setError(deleteError);
  }, [deleteError]);

  if (
    ![
      'detail',
      'settings',
      'board',
      'swimlane',
      'trace',
      'hazardTrace',
      'userNeed',
      'designInput',
      'designOutput',
      'validation',
      'verification',
      'designSpec',
      'hazard'
    ].includes(view)
  ) {
    return <Redirect to={`/portal/products/${productId}/trace`} />;
  }

  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton to='/portal/products' />
        <ProductHeaderTitle product={product} onChange={onChange}>
          <h2 className='title' title={product && 'Click to edit'}>
            {product?.name
              ? `PRODUCT-${productId} ${product?.name}`
              : 'Loading...'}
          </h2>
        </ProductHeaderTitle>
        <Box ml={2}>
          <Button
            color='primary'
            variant='contained'
            className='flex-label'
            endIcon={<ArrowDropDownIcon />}
            onClick={event => setMenuAnchorEl(event.currentTarget)}
          >
            View
          </Button>
          <Menu
            id='product-view-menu'
            keepMounted
            open={!!menuAnchorEl}
            anchorEl={menuAnchorEl}
            onClose={() => setMenuAnchorEl(null)}
          >
            {views.map(view => {
              return (
                <MenuItem
                  key={view.name}
                  onClick={() => {
                    gotoPage(view.path as PageType);
                    setMenuAnchorEl(null);
                  }}
                >
                  {view.name}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>

        {view === 'detail' ? (
          <div style={{ flex: 1 }} />
        ) : (
          <>
            <SearchBox value={search} onSearch={setSearch}>
              {view === 'hazardTrace' ? (
                <HazardBoardFilter
                  options={hazardFilter.options}
                  filterHazards={true}
                  onFilter={(options: HazardFilterOptions) => {
                    const params: ApiParams =
                      hazardFilter.convertOptionsToParams(options);
                    const { search } = apiParams;
                    search && (params.search = search);
                    hazardFilter.setStatus(params.status);
                    hazardFilter.setAssigneeId(params.assigneeId);
                    hazardFilter.setAuthorId(params.authorId);
                    hazardFilter.setAssignedForReviewOnly(
                      params.assignedForReviewOnly
                    );
                    setApiParams(params);
                  }}
                />
              ) : (
                <ProductBoardFilter
                  productId={productId}
                  options={productFilter.options}
                  onFilter={(options: ProductFilterOptions) => {
                    const params: ApiParams =
                      productFilter.convertOptionsToParams(options);
                    const { search } = apiParams;
                    search && (params.search = search);
                    productFilter.setStatus(params.status);
                    productFilter.setAssigneeId(params.assigneeId);
                    productFilter.setComponents(params.components);
                    productFilter.setReleases(params.releases);
                    productFilter.setAssignedForReviewOnly(
                      params.assignedForReviewOnly
                    );
                    productFilter.setUnassignedOnly(params.unassignedOnly);
                    setApiParams(params);
                  }}
                />
              )}
            </SearchBox>
          </>
        )}
        <PageTemplateHeaderActions>
          {product?.task && (
            <Route exact path='/portal/products/:productId/detail'>
              <TaskStatusActions
                task={product.task}
                taskEntity={product}
                onChange={task => {
                  product.task = task;
                  setProduct({ ...product });
                }}
              />
            </Route>
          )}
          {view && product && (
            <ProductEntityCreateButton
              page={view}
              productId={product.id}
              onCreate={() => fetchRef.current?.()}
            />
          )}
          {product && (
            <ProductExportButton
              product={product}
              apiParams={apiParams}
              fetchRef={fetchTemplatesRef}
            />
          )}
          {!isDetailPage && <CartIconBadge />}
          {/* always show menu icon button */}
          <ListMoreButtonMenu>
            {/* only display 'Show/Hide Deleted' when not in detail page */}
            {product && (
              <>
                {!isDetailPage && (
                  <ListItem
                    button
                    onClick={() => {
                      if (apiParams.includeDeleted) {
                        delete apiParams.includeDeleted;
                      } else {
                        apiParams.includeDeleted = true;
                      }
                      setApiParams({ ...apiParams });
                    }}
                  >
                    <ListItemText
                      primary={`${
                        apiParams.includeDeleted ? 'Hide' : 'Show'
                      } Deleted`}
                    />
                  </ListItem>
                )}
                <ListItem button onClick={() => setOpenArchiveDialog(true)}>
                  <ListItemText
                    primary={
                      (product.deleted ? 'Unarchive' : 'Archive') + ' Product'
                    }
                  />
                </ListItem>
              </>
            )}
            <ListItem
              button
              onClick={() => history.push(getPageUrl('settings'))}
            >
              <ListItemText primary='Settings' />
            </ListItem>
          </ListMoreButtonMenu>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>

      {isDetailPage && (
        <PageTemplateNav tabs>
          <Tabs
            value={view}
            variant='scrollable'
            scrollButtons='auto'
            indicatorColor='primary'
          >
            <Tab
              component={Link}
              label='Details'
              value='detail'
              to={getPageUrl('detail')}
            />
            <Tab
              component={Link}
              label={getProductEntityName('userNeed')}
              value='userNeed'
              to={getPageUrl('userNeed')}
            />
            <Tab
              component={Link}
              label={getProductEntityName('designInput')}
              value='designInput'
              to={getPageUrl('designInput')}
            />
            <Tab
              component={Link}
              label={getProductEntityName('designOutput')}
              value='designOutput'
              to={getPageUrl('designOutput')}
            />
            <Tab
              component={Link}
              label={getProductEntityName('verification')}
              value='verification'
              to={getPageUrl('verification')}
            />
            <Tab
              component={Link}
              label={getProductEntityName('validation')}
              value='validation'
              to={getPageUrl('validation')}
            />
            {environment?.productConfig?.designSpec && (
              <Tab
                component={Link}
                label={getProductEntityName('designSpec')}
                value='designSpec'
                to={getPageUrl('designSpec')}
              />
            )}
            <Tab
              component={Link}
              label='Hazards'
              value='hazard'
              to={getPageUrl('hazard')}
            />
            {/* Move the settings to ListMoreButtonMenu */}
            {/* <Tab
              component={Link}
              label='Settings'
              value='settings'
              to={getPageUrl('settings')}
            /> */}
          </Tabs>
        </PageTemplateNav>
        // <Divider />
      )}

      <PageTemplateDashboard
        innerRef={scrollRef}
        className={!isDetailPage ? 'board-view' : ''}
      >
        {product && (
          <>
            <Route exact path='/portal/products/:productId/userNeed'>
              <UserNeedsTable {...tableProps} />
            </Route>
            <Route exact path='/portal/products/:productId/designInput'>
              <DesignInputsTable {...tableProps} />
            </Route>
            <Route exact path='/portal/products/:productId/designOutput'>
              <DesignOutputsTable {...tableProps} />
            </Route>
            <Route exact path='/portal/products/:productId/validation'>
              <ValidationsTable {...tableProps} />
            </Route>
            <Route exact path='/portal/products/:productId/verification'>
              <VerificationsTable {...tableProps} />
            </Route>
            <Route exact path='/portal/products/:productId/designSpec'>
              <DesignSpecificationTable {...tableProps} />
            </Route>
            <Route exact path='/portal/products/:productId/hazard'>
              <HazardsTable {...tableProps} />
            </Route>
            <Route exact path='/portal/products/:productId/detail'>
              <ProductDetailPage product={product} onChange={onChange} />
            </Route>
            <Route exact path='/portal/products/:productId/settings'>
              <ManageTemplateCard
                showReference
                product={product}
                onUpdate={() => fetchTemplatesRef.current?.()}
                onProductChange={onChange}
              />
            </Route>
            <Route exact path='/portal/products/:productId/board'>
              <ProductBoard product={product} apiParams={apiParams} />
            </Route>
            <Route exact path='/portal/products/:productId/swimlane'>
              <ProductBoardSwimlane
                product={product}
                apiParams={apiParams}
                scrollRef={scrollRef}
              />
            </Route>
            <Route exact path='/portal/products/:productId/trace'>
              <ProductBoardTrace product={product} apiParams={apiParams} />
            </Route>
            <Route exact path='/portal/products/:productId/hazardTrace'>
              <ProductHazardBoardTrace
                product={product}
                apiParams={apiParams}
              />
            </Route>
          </>
        )}
      </PageTemplateDashboard>

      <ConfirmDialog
        open={!!product && openArchiveDialog}
        title={(product?.deleted ? 'Unarchive' : 'Archive') + ' Product'}
        message={`Are you sure you want to ${
          product?.deleted ? 'unarchive' : 'archive'
        } this Product?`}
        onCancel={() => setOpenArchiveDialog(false)}
        onConfirm={() => {
          setOpenArchiveDialog(false);
          if (product?.deleted) {
            updateProduct({ deleted: false });
          } else {
            deleteProduct();
          }
        }}
      />

      <AppSnackbar
        open={snackbar.open}
        severity={snackbar.severity}
        onClose={() => setSnackbar(snackbar => ({ ...snackbar, open: false }))}
      >
        {snackbar.message}
      </AppSnackbar>
    </StyledPageTemplate>
  );
};

export default ProductPage;
